// src/features/rrhh/horarios/components/CrearHorarioDrawer.jsx
import { useEffect, useMemo, useState } from "react";
import AddCircle from "@mui/icons-material/AddCircle";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { crearHorariosDesdePlan } from "../api/horariosApi";

/* ========= helpers ========= */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
// Semana Lunes->Domingo
function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0 dom, 1 lun...
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(x, diff);
}
function dayNameFromKey(k) {
  const map = {
    1: "Lunes",
    2: "Martes",
    3: "Miércoles",
    4: "Jueves",
    5: "Viernes",
    6: "Sábado",
    7: "Domingo",
  };
  return map[k] || "Día";
}
function normalizeTime(s) {
  const v = String(s || "").trim();
  if (!v) return "";
  // fuerza HH:mm
  if (/^\d{2}:\d{2}$/.test(v)) return v;
  if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v.slice(0, 5);
  return v;
}

/* ========= UI shared styles (igual a tu drawer) ========= */
const labelSx = {
  fontFamily: '"Open Sans", Helvetica',
  fontSize: "16px",
  fontWeight: 600,
  color: "primary.900",
};

const inputBaseSx = {
  height: "30px",
  bgcolor: "neutral.50",
  borderRadius: "10px",
  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& input": {
    fontFamily: '"Open Sans", Helvetica',
    fontSize: "14px",
    fontWeight: 300,
    color: "primary.900",
    padding: "8px 10px",
  },
};

const selectBaseSx = {
  height: "30px",
  bgcolor: "neutral.50",
  borderRadius: "10px",
  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    fontFamily: '"Open Sans", Helvetica',
    fontSize: "14px",
    fontWeight: 300,
    color: "primary.900",
    padding: "8px 10px",
  },
};

const menuPaperSx = {
  bgcolor: "neutral.50",
  borderRadius: "10px",
  boxShadow: "0px 0px 4px 0px rgba(117, 117, 138, 1)",
  "& .MuiMenuItem-root": {
    fontFamily: '"Open Sans", Helvetica',
    fontSize: "14px",
    fontWeight: 400,
    color: "primary.900",
    height: "30px",
    px: 1,
    py: 0.5,
  },
};

const WEEK = [
  { key: 1, label: "L" },
  { key: 2, label: "M" },
  { key: 3, label: "X" },
  { key: 4, label: "J" },
  { key: 5, label: "V" },
  { key: 6, label: "S" },
  { key: 7, label: "D" },
];

const TIPO_DIA_OPTIONS = [
  { value: "NORMAL", label: "Normal" },
  { value: "FRANCO", label: "Franco" },
  { value: "NINGUNO", label: "Ninguno" },
];

const TIPO_HORARIO_OPTIONS = [
  { value: "DIURNO", label: "Diurno" },
  { value: "NOCTURNO", label: "Nocturno" },
  { value: "MIXTO", label: "Mixto" },
];

function defaultDias() {
  // L-V normal, S-D franco
  return WEEK.map((d) => {
    const isWeekend = d.key >= 6;
    return {
      dia_semana: d.key,
      tipo_dia: isWeekend ? "FRANCO" : "NORMAL",
      tipo_horario: "DIURNO",
      turnos: isWeekend
        ? []
        : [{ hora_entrada: "09:00", hora_salida: "13:00" }],
    };
  });
}

function chipLikeButtonSx(selected) {
  return {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    outline: "none",
    width: "100%",
    "& > div": {
      px: 2,
      py: 1,
      bgcolor: selected ? "primary.400" : "secondary.200",
      borderRadius: "33px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
      transition: "transform .12s ease",
      width: "100%",
    },
    "&:hover > div": { transform: "translateY(-1px)" },
    "& span": {
      fontFamily: '"Open Sans", Helvetica',
      fontSize: "14px",
      fontWeight: 700,
      color: selected ? "logoblanco" : "primary.900",
    },
  };
}

function dayChipSx(cfg, isSel) {
  const tipo = cfg?.tipo_dia;

  const bg =
    tipo === "FRANCO"
      ? "primary.400"
      : tipo === "NORMAL"
        ? "secondary.200"
        : "neutral.50";

  const labelColor = tipo === "FRANCO" ? "logoblanco" : "primary.900";

  return {
    flex: 1,
    justifyContent: "center",
    bgcolor: bg,
    borderRadius: "14px",
    boxShadow: isSel
      ? "0px 0px 0px 2px rgba(17,44,110,.35), inset 0px 0px 3px rgba(142, 142, 159, 0.5)"
      : "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
    "& .MuiChip-label": {
      fontFamily: '"Open Sans", Helvetica',
      fontSize: "14px",
      fontWeight: 800,
      color: labelColor,
      padding: 0,
    },
  };
}

function safeStringify(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v instanceof Error) return v.message || String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function pickDetail(e) {
  // axios: e.response.data
  const data = e?.response?.data ?? e?.data ?? null;
  return data?.detail ?? data ?? e;
}

function buildErrMsg(e) {
  const status = e?.response?.status ?? e?.status;
  const detail = pickDetail(e);

  // Caso 409 con tu formato esperado
  if (status === 409) {
    const msg =
      (typeof detail?.message === "string" && detail.message) ||
      (typeof detail?.detail === "string" && detail.detail) ||
      "Conflicto: ya existe un horario en ese rango.";

    const arr = detail?.conflictos || detail?.conflicts || [];
    const conflicts = Array.isArray(arr)
      ? arr
          .map((c) => {
            const fin = c?.fecha_fin ? c.fecha_fin : "sin fin";
            return `• Empleado #${c?.empleado_id ?? "?"}: ${c?.fecha_inicio ?? "?"} a ${fin} (horario #${c?.horario_existente_id ?? "?"})`;
          })
          .join("\n")
      : "";

    return `${msg}${conflicts ? "\n" + conflicts : ""}`.trim();
  }

  // Otros errores
  const msg =
    (typeof detail === "string" && detail) ||
    (typeof detail?.message === "string" && detail.message) ||
    (typeof detail?.detail === "string" && detail.detail) ||
    (typeof e?.message === "string" && e.message) ||
    safeStringify(detail);

  return msg || "No se pudo crear el horario.";
}

export default function CrearHorarioDrawer({
  open,
  onClose,
  empleados = [],
  areas = [],
  defaultCursorDate, // opcional: para sugerir semana actual del page
  onCreated, // opcional
}) {
  const [scope, setScope] = useState("empleado"); // empleado | area

  const [empleadoObj, setEmpleadoObj] = useState(null);
  const [areaId, setAreaId] = useState("");

  const [semanaInicio, setSemanaInicio] = useState(() => {
    const base = defaultCursorDate ? new Date(defaultCursorDate) : new Date();
    return toYMD(startOfWeekMonday(base));
  });

  const [repetirUnidad, setRepetirUnidad] = useState("SEMANAS"); // SEMANAS | MESES
  const [repetirCantidad, setRepetirCantidad] = useState(1);
  const [trabajaFeriados, setTrabajaFeriados] = useState(true);

  const [dias, setDias] = useState(() => defaultDias());
  const [dayFocus, setDayFocus] = useState(1);

  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [showAreaEmps, setShowAreaEmps] = useState(false);

  // si cambia el scope, limpio campos “del otro modo”
  useEffect(() => {
    setErrMsg("");
    setOkMsg("");
    if (scope === "empleado") {
      setAreaId("");
      setShowAreaEmps(false);
    } else {
      setEmpleadoObj(null);
    }
  }, [scope]);

  // si abro, reseteo mensajes
  useEffect(() => {
    if (!open) return;
    setErrMsg("");
    setOkMsg("");
  }, [open]);

  const empleadoAreaNombre = useMemo(() => {
    const a = empleadoObj?.area || empleadoObj?.area_nombre;
    return a ? String(a) : "";
  }, [empleadoObj]);

  const selectedAreaObj = useMemo(
    () => (areas || []).find((a) => String(a?.id) === String(areaId)) || null,
    [areas, areaId]
  );

  const empleadosEnArea = useMemo(() => {
    if (scope !== "area") return [];
    const areaName = selectedAreaObj?.nombre;
    if (!areaName) return [];
    return (empleados || []).filter(
      (e) => String(e?.area || e?.area_nombre || "").toLowerCase() === String(areaName).toLowerCase()
    );
  }, [scope, selectedAreaObj, empleados]);

  const empleadosAfectados = empleadosEnArea.length;

  const dayData = useMemo(() => dias.find((d) => d.dia_semana === dayFocus), [dias, dayFocus]);

  function updateDay(patch) {
    setDias((prev) => prev.map((d) => (d.dia_semana === dayFocus ? { ...d, ...patch } : d)));
  }

  function copyDayToAll() {
    const src = dayData;
    if (!src) return;
    setDias((prev) =>
      prev.map((d) => ({
        ...d,
        tipo_dia: src.tipo_dia,
        tipo_horario: src.tipo_horario,
        turnos:
          src.tipo_dia === "NORMAL" ? (src.turnos || []).map((t) => ({ ...t })) : [],
      }))
    );
    setOkMsg("Configuración copiada a todos los días.");
  }

  function resetDayToDefault() {
    const fresh = defaultDias().find((d) => d.dia_semana === dayFocus);
    if (!fresh) return;
    updateDay(fresh);
  }

  function setTipoDia(next) {
    if (!next) return;

    const wasNormal = dayData?.tipo_dia === "NORMAL";
    const existing = Array.isArray(dayData?.turnos) ? dayData.turnos : [];

    updateDay({
      tipo_dia: next,
      tipo_horario: dayData?.tipo_horario || "DIURNO",
      turnos:
        next === "NORMAL"
          ? existing.length
            ? existing
            : [{ hora_entrada: "09:00", hora_salida: "13:00" }]
          : [],
    });

    if (wasNormal && next !== "NORMAL") {
      setOkMsg("Se ocultaron los turnos (día no laboral).");
    }
  }

  function addTurno() {
    const list = Array.isArray(dayData?.turnos) ? dayData.turnos : [];
    updateDay({
      turnos: [...list, { hora_entrada: "09:00", hora_salida: "13:00" }].slice(0, 2),
    });
  }

  function removeTurno(idx) {
    const list = Array.isArray(dayData?.turnos) ? dayData.turnos : [];
    updateDay({ turnos: list.filter((_, i) => i !== idx) });
  }

  function updateTurno(idx, patch) {
    const list = Array.isArray(dayData?.turnos) ? dayData.turnos : [];
    updateDay({
      turnos: list.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    });
  }

  function normalizeSemanaInicioYMD(ymd) {
    // fuerza lunes
    const [y, m, d] = String(ymd).split("-").map(Number);
    const base = new Date(y, (m || 1) - 1, d || 1);
    return toYMD(startOfWeekMonday(base));
  }

  function validate() {
    if (scope === "empleado") {
      const empId = empleadoObj?.id ?? empleadoObj?.empleado_id ?? empleadoObj?.id_empleado;
      if (!empId) return "Seleccioná un empleado.";
    }
    if (scope === "area") {
      if (!areaId) return "Seleccioná un área.";
    }
    if (!semanaInicio) return "Ingresá la semana de inicio (lunes).";

    const cant = Number(repetirCantidad);
    if (!Number.isFinite(cant) || cant < 1) return "Repetir: cantidad debe ser >= 1.";

    for (const d of dias) {
      if (d.tipo_dia !== "NORMAL") continue;
      const ts = Array.isArray(d.turnos) ? d.turnos : [];
      if (ts.length < 1) return "En los días NORMAL debe haber al menos 1 turno.";

      for (const t of ts) {
        const a = normalizeTime(t?.hora_entrada);
        const b = normalizeTime(t?.hora_salida);
        if (!a || !b) return "Cada turno debe tener hora de entrada y salida.";
      }
    }
    return "";
  }

  async function handleSave() {
    setErrMsg("");
    setOkMsg("");

    const v = validate();
    if (v) {
      setErrMsg(v);
      return;
    }

    const empId = empleadoObj?.id ?? empleadoObj?.empleado_id ?? empleadoObj?.id_empleado;

    const payload = {
      scope,
      empleado_id: scope === "empleado" ? Number(empId) : null,
      area_id: scope === "area" ? Number(areaId) : null,
      semana_inicio: normalizeSemanaInicioYMD(semanaInicio),
      repetir: {
        unidad: repetirUnidad,
        cantidad: Number(repetirCantidad),
      },
      trabaja_feriados: Boolean(trabajaFeriados),
      dias: (dias || []).map((d) => ({
        dia_semana: d.dia_semana,
        tipo_dia: d.tipo_dia,
        tipo_horario: d.tipo_horario || "DIURNO",
        turnos:
          d.tipo_dia === "NORMAL"
            ? (d.turnos || []).map((t) => ({
                hora_entrada: normalizeTime(t.hora_entrada),
                hora_salida: normalizeTime(t.hora_salida),
              }))
            : [],
      })),
    };

    try {
      setSaving(true);
      await crearHorariosDesdePlan(payload);
      setOkMsg("Horario creado correctamente.");
      if (onCreated) onCreated(payload);
    } catch (e) {
      // Debug útil (mirá la consola para ver la forma real del error)
      console.log("ERROR crear horario:", e);
      console.log("STATUS:", e?.response?.status, "DATA:", e?.response?.data);

      setErrMsg(buildErrMsg(e));

    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "80%", md: "44%", lg: "32%" },
          height: "100vh",
          bgcolor: "neutral.200",
          overflowY: "auto",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 2,
          position: "relative",
        }}
      >
        {/* Close */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 10, right: 10, bgcolor: "neutral.50" }}
        >
          <CloseIcon sx={{ color: "primary.900" }} />
        </IconButton>

        <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              bgcolor: "primary.400",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AddCircle sx={{ fontSize: "44px", color: "logoblanco" }} />
          </Box>

          <Chip
            label="Crear horario"
            sx={{
              bgcolor: "secondary.50",
              boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
              borderRadius: "33px",
              px: 2,
              py: 1,
              "& .MuiChip-label": {
                fontFamily: '"Open Sans", Helvetica',
                fontSize: "14px",
                fontWeight: 600,
                color: "primary.900",
                padding: 0,
              },
            }}
          />

          <Typography
            sx={{
              fontFamily: '"Open Sans", Helvetica',
              fontSize: "14px",
              fontWeight: 600,
              color: "primary.900",
              textAlign: "left",
              width: "100%",
            }}
          >
            Definí el alcance, la vigencia y la configuración día por día.
          </Typography>

          {/* Mensajes */}
          {errMsg ? (
            <Box
              sx={{
                width: "100%",
                p: 1,
                borderRadius: "12px",
                bgcolor: "secondary.50",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Open Sans", Helvetica',
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "error.main",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {errMsg}
              </Typography>
            </Box>
          ) : null}

          {okMsg ? (
            <Box
              sx={{
                width: "100%",
                p: 1,
                borderRadius: "12px",
                bgcolor: "secondary.50",
                boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Open Sans", Helvetica',
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "success.main",
                }}
              >
                {okMsg}
              </Typography>
            </Box>
          ) : null}

          {/* ===== Selector Alcance (Empleado / Área) ===== */}
          <Stack spacing={0.5} sx={{ width: "100%" }}>
            <Typography sx={labelSx}>Alcance del horario: *</Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                width: "100%",
                p: 0.8,
                bgcolor: "neutral.50",
                borderRadius: "20px",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
              }}
            >
              <Box component="button" onClick={() => setScope("empleado")} style={{ border: "none", background: "transparent", padding: 0, flex: 1 }}>
                <Box sx={chipLikeButtonSx(scope === "empleado")}>
                  <div><span>Empleado</span></div>
                </Box>
              </Box>

              <Box component="button" onClick={() => setScope("area")} style={{ border: "none", background: "transparent", padding: 0, flex: 1 }}>
                <Box sx={chipLikeButtonSx(scope === "area")}>
                  <div><span>Área</span></div>
                </Box>
              </Box>
            </Stack>
          </Stack>

          {/* ===== MODO EMPLEADO ===== */}
          {scope === "empleado" ? (
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Stack spacing={0.5}>
                <Typography sx={labelSx}>Empleado objetivo: *</Typography>

                <Autocomplete
                  value={empleadoObj}
                  onChange={(_, v) => setEmpleadoObj(v)}
                  options={empleados || []}
                  getOptionLabel={(o) => String(o?.nombre || o?.name || "")}
                  isOptionEqualToValue={(a, b) =>
                    String(a?.id ?? a?.id_empleado ?? a?.empleado_id) ===
                    String(b?.id ?? b?.id_empleado ?? b?.empleado_id)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar empleado..."
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          ...inputBaseSx,
                          "& input": inputBaseSx["& input"],
                        },
                      }}
                    />
                  )}
                />
              </Stack>

              {/* Área del empleado (solo lectura) */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ ...labelSx, fontSize: "14px" }}>Área:</Typography>
                <Chip
                  label={empleadoAreaNombre || "—"}
                  sx={{
                    bgcolor: "secondary.200",
                    boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                    borderRadius: "33px",
                    px: 1.5,
                    py: 0.5,
                    "& .MuiChip-label": {
                      fontFamily: '"Open Sans", Helvetica',
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "primary.900",
                      padding: 0,
                    },
                  }}
                />
              </Stack>
            </Stack>
          ) : null}

          {/* ===== MODO ÁREA ===== */}
          {scope === "area" ? (
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Stack spacing={0.5}>
                <Typography sx={labelSx}>Área: *</Typography>
                <Select
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value)}
                  displayEmpty
                  fullWidth
                  IconComponent={ArrowDropDown}
                  sx={selectBaseSx}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                >
                  <MenuItem value="" disabled>Seleccionar un área</MenuItem>
                  {(areas || []).map((a) => (
                    <MenuItem key={a?.id} value={String(a?.id)}>{a?.nombre}</MenuItem>
                  ))}
                </Select>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ ...labelSx, fontSize: "14px" }}>Empleados afectados:</Typography>
                  <Chip
                    label={`${empleadosAfectados || 0}`}
                    sx={{
                      bgcolor: "primary.400",
                      borderRadius: "33px",
                      px: 2,
                      py: 1,
                      "& .MuiChip-label": {
                        fontFamily: '"Open Sans", Helvetica',
                        fontSize: "13px",
                        fontWeight: 800,
                        color: "logoblanco",
                        padding: 0,
                      },
                    }}
                  />
                </Stack>

                <Chip
                  clickable
                  onClick={() => setShowAreaEmps((v) => !v)}
                  label={showAreaEmps ? "Ocultar" : "Ver empleados"}
                  sx={{
                    bgcolor: "secondary.200",
                    borderRadius: "33px",
                    px: 2,
                    py: 1,
                    "& .MuiChip-label": {
                      fontFamily: '"Open Sans", Helvetica',
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "primary.900",
                      padding: 0,
                    },
                  }}
                />
              </Stack>

              {showAreaEmps ? (
                <Box
                  sx={{
                    width: "100%",
                    p: 1,
                    bgcolor: "neutral.50",
                    borderRadius: "12px",
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    maxHeight: 140,
                    overflowY: "auto",
                  }}
                >
                  {(empleadosEnArea || []).length ? (
                    (empleadosEnArea || []).map((e) => (
                      <Typography
                        key={String(e?.id ?? e?.id_empleado ?? e?.empleado_id)}
                        sx={{
                          fontFamily: '"Open Sans", Helvetica',
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "primary.900",
                          py: 0.25,
                        }}
                      >
                        • {String(e?.nombre || e?.name || "Empleado")}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: '"Open Sans", Helvetica',
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "primary.900",
                        opacity: 0.7,
                      }}
                    >
                      No hay empleados en esa área (o falta mapear el campo area).
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Stack>
          ) : null}

          {/* ===== Vigencia ===== */}
          <Stack spacing={1} sx={{ width: "100%", mt: 0.5 }}>
            <Box>
              <Chip
                label="Vigencia"
                sx={{
                  bgcolor: "secondary.50",
                  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                  borderRadius: "33px",
                  px: 2,
                  py: 1,
                  mt: 0.5,
                  "& .MuiChip-label": {
                    fontFamily: '"Open Sans", Helvetica',
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "primary.900",
                    padding: 0,
                  },
                }}
              />
            </Box>

            <Stack spacing={0.5}>
              <Typography sx={labelSx}>Semana inicio (Lunes): *</Typography>
              <TextField
                type="date"
                value={semanaInicio}
                onChange={(e) => setSemanaInicio(normalizeSemanaInicioYMD(e.target.value))}
                fullWidth
                InputProps={{ sx: inputBaseSx }}
              />
              <Typography
                sx={{
                  fontFamily: '"Open Sans", Helvetica',
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "primary.900",
                  opacity: 0.7,
                }}
              >
                Se ajusta automáticamente al lunes de esa semana.
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography sx={labelSx}>Repetir:</Typography>
                <TextField
                  value={repetirCantidad}
                  onChange={(e) => setRepetirCantidad(e.target.value)}
                  placeholder="1"
                  fullWidth
                  InputProps={{ sx: inputBaseSx }}
                />
              </Stack>

              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography sx={labelSx}>Unidad:</Typography>
                <Select
                  value={repetirUnidad}
                  onChange={(e) => setRepetirUnidad(e.target.value)}
                  fullWidth
                  IconComponent={ArrowDropDown}
                  sx={selectBaseSx}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                >
                  <MenuItem value="SEMANAS">Semanas</MenuItem>
                  <MenuItem value="MESES">Meses</MenuItem>
                </Select>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ ...labelSx, fontSize: "14px" }}>Trabaja feriados:</Typography>
              <Chip
                clickable
                onClick={() => setTrabajaFeriados((v) => !v)}
                label={trabajaFeriados ? "Sí" : "No"}
                sx={{
                  bgcolor: trabajaFeriados ? "primary.400" : "secondary.200",
                  borderRadius: "33px",
                  px: 2,
                  py: 1,
                  "& .MuiChip-label": {
                    fontFamily: '"Open Sans", Helvetica',
                    fontSize: "13px",
                    fontWeight: 800,
                    color: trabajaFeriados ? "logoblanco" : "primary.900",
                    padding: 0,
                  },
                }}
              />
            </Stack>
          </Stack>

          {/* ===== Chips L–D + editor día por día ===== */}
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Box>
              <Chip
                label="Días"
                sx={{
                  bgcolor: "secondary.50",
                  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                  borderRadius: "33px",
                  px: 2,
                  py: 1,
                  mt: 0.5,
                  "& .MuiChip-label": {
                    fontFamily: '"Open Sans", Helvetica',
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "primary.900",
                    padding: 0,
                  },
                }}
              />
            </Box>

            {/* selector de día */}
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              {WEEK.map((d) => {
                const cfg = dias.find((x) => x.dia_semana === d.key);
                const isSel = dayFocus === d.key;
                return (
                  <Chip
                    key={d.key}
                    clickable
                    onClick={() => setDayFocus(d.key)}
                    label={d.label}
                    sx={dayChipSx(cfg, isSel)}
                  />
                );
              })}
            </Stack>

            {/* Editor del día seleccionado */}
            <Box
              sx={{
                width: "100%",
                p: 1.2,
                bgcolor: "neutral.50",
                borderRadius: "16px",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", Helvetica',
                      fontSize: "14px",
                      fontWeight: 800,
                      color: "primary.900",
                    }}
                  >
                    {dayNameFromKey(dayFocus)}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Chip
                      clickable
                      onClick={copyDayToAll}
                      label="Copiar a todos"
                      icon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        bgcolor: "secondary.200",
                        borderRadius: "33px",
                        px: 1.5,
                        py: 1,
                        "& .MuiChip-label": {
                          fontFamily: '"Open Sans", Helvetica',
                          fontSize: "13px",
                          fontWeight: 800,
                          color: "primary.900",
                        },
                      }}
                    />
                    <Chip
                      clickable
                      onClick={resetDayToDefault}
                      label="Reset"
                      sx={{
                        bgcolor: "neutral.200",
                        borderRadius: "33px",
                        px: 1.5,
                        py: 1,
                        "& .MuiChip-label": {
                          fontFamily: '"Open Sans", Helvetica',
                          fontSize: "13px",
                          fontWeight: 800,
                          color: "primary.900",
                        },
                      }}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={0.5}>
                  <Typography sx={labelSx}>Tipo de día: *</Typography>
                  <Select
                    value={dayData?.tipo_dia || "NORMAL"}
                    onChange={(e) => setTipoDia(e.target.value)}
                    fullWidth
                    IconComponent={ArrowDropDown}
                    sx={selectBaseSx}
                    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                  >
                    {TIPO_DIA_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                {dayData?.tipo_dia === "NORMAL" ? (
                  <>
                    <Stack spacing={0.5}>
                      <Typography sx={labelSx}>Tipo de horario:</Typography>
                      <Select
                        value={dayData?.tipo_horario || "DIURNO"}
                        onChange={(e) => updateDay({ tipo_horario: e.target.value })}
                        fullWidth
                        IconComponent={ArrowDropDown}
                        sx={selectBaseSx}
                        MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                      >
                        {TIPO_HORARIO_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>

                    <Box>
                      <Chip
                        label="Turnos"
                        sx={{
                          bgcolor: "secondary.50",
                          boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                          borderRadius: "33px",
                          px: 2,
                          py: 1,
                          mt: 0.5,
                          "& .MuiChip-label": {
                            fontFamily: '"Open Sans", Helvetica',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "primary.900",
                            padding: 0,
                          },
                        }}
                      />
                    </Box>

                    <Stack spacing={1}>
                      {(dayData?.turnos || []).map((t, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            p: 1,
                            bgcolor: "neutral.200",
                            borderRadius: "12px",
                            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                          }}
                        >
                          <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Typography sx={{ ...labelSx, fontSize: "13px" }}>Entrada</Typography>
                            <TextField
                              type="time"
                              value={normalizeTime(t?.hora_entrada)}
                              onChange={(e) => updateTurno(idx, { hora_entrada: e.target.value })}
                              fullWidth
                              InputProps={{ sx: inputBaseSx }}
                            />
                          </Stack>

                          <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Typography sx={{ ...labelSx, fontSize: "13px" }}>Salida</Typography>
                            <TextField
                              type="time"
                              value={normalizeTime(t?.hora_salida)}
                              onChange={(e) => updateTurno(idx, { hora_salida: e.target.value })}
                              fullWidth
                              InputProps={{ sx: inputBaseSx }}
                            />
                          </Stack>

                          <IconButton
                            onClick={() => removeTurno(idx)}
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: "neutral.50",
                              "&:hover": { bgcolor: "neutral.100" },
                            }}
                          >
                            <DeleteIcon sx={{ color: "primary.900", fontSize: 20 }} />
                          </IconButton>
                        </Stack>
                      ))}

                      <Button
                        variant="contained"
                        disabled={(dayData?.turnos || []).length >= 3}
                        onClick={addTurno}
                        sx={{
                          bgcolor: "primary.400",
                          borderRadius: "33px",
                          px: 2,
                          py: 1,
                          fontFamily: '"Open Sans", Helvetica',
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "logoblanco",
                          textTransform: "none",
                          "&:hover": { bgcolor: "primary.500" },
                          "&.Mui-disabled": { bgcolor: "secondary.200", color: "primary.900" },
                        }}
                      >
                        Agregar turno (máx. 2)
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", Helvetica',
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "primary.900",
                      opacity: 0.7,
                    }}
                  >
                    Este día no tiene turnos (tipo: {dayData?.tipo_dia}).
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Stack>

        {/* Botón guardar */}
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          sx={{
            width: "100%",
            bgcolor: "primary.400",
            borderRadius: "33px",
            px: 2,
            py: 1.2,
            fontFamily: '"Open Sans", Helvetica',
            fontSize: "14px",
            fontWeight: 800,
            color: "logoblanco",
            textTransform: "none",
            "&:hover": { bgcolor: "primary.500" },
            "&.Mui-disabled": { bgcolor: "secondary.200", color: "primary.900" },
          }}
        >
          {saving ? "Guardando..." : "Guardar Horario"}
        </Button>
      </Box>
    </Drawer>
  );
}
